import _ from 'underscore';

import { FeatureFlags } from '~src/ts/FeatureFlags';
import { ContentMapper } from '~src/ts/mappers/ContentMapper';
import { LocationMapper } from '~src/ts/mappers/LocationMapper';
import { MapperHelper } from '~src/ts/mappers/MapperHelper';
import { ProjectMapper } from '~src/ts/mappers/ProjectMapper';
import { UserMapper } from '~src/ts/mappers/UserMapper';
import { ILocationInfo } from '~src/ts/models/ILocationInfo';
import { IUserInfo } from '~src/ts/models/IUserInfo';
import { INamedItemInfo } from '~src/ts/models/resourceInfoParts';
import { IWorkbookUsageInfo } from '~src/ts/ServerApi/types/IUsageInfo';

import { IWorkbook, IWorkbookDetail, IWorkbookParent } from '../../ServerApi/types/IWorkbook';
import { IWorkbookDetailInfo, IWorkbookInfo, IWorkbookParentInfo } from '../IWorkbookInfo';

export module WorkbookMapper {

  const M = MapperHelper;

  export function map(
    serverApiWorkbooks: IWorkbook[],
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo>,
    locations?: ILocationInfo[],
    usageInfoMaxValues?: IWorkbookUsageInfo,
    favoriteWorkbooks: _.Dictionary<any> = {}
  ): IWorkbookInfo[] {
    const workbooksToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.workbook, projects);
    const workbooksToLocation = ContentMapper.getContentToLocationMap(ContentMapper.ContentTypeToTargetMap.workbook, locations);
    return _.map(serverApiWorkbooks, (w: IWorkbook) =>
      mapOne(w, users[w.ownerId], users[w.modifiedByUserId],
        (FeatureFlags.refactorProjectEnabled) ? workbooksToProject[w.id] : projects[w.projectId],
        (FeatureFlags.personalSpaceEnabled) ? workbooksToLocation[w.id] : undefined,
        usageInfoMaxValues, !!favoriteWorkbooks[w.id]));
  }

  export function mapOne(
    w: IWorkbook,
    owner: IUserInfo,
    modifiedByUser: IUserInfo,
    project: INamedItemInfo,
    location?: ILocationInfo,
    usageInfoMaxValues?: IWorkbookUsageInfo,
    favorite?: boolean
  ): IWorkbookInfo {
    return {
      defaultViewId:          w.defaultViewId,
      description:            w.description,
      displayTabs:            w.displayTabs,
      downloadUrl:            w.downloadUrl,
      extractCreationPending: w.extractCreationPending,
      extractEncryptionState: w.extractEncryptionState,
      favorite:               favorite,
      hasAlert:               w.hasAlert,
      hasExtracts:            w.hasExtracts,
      hasIncrementalExtract:  w.hasIncrementalExtract,
      id:                     w.id,
      luid:                   w.luid,
      lastRefreshedAt:        M.toDate(w.lastRefreshedAt),
      name:                   w.name,
      numSheets:              M.toNumber(w.sheetCount),
      owner:                  UserMapper.mapOne(owner),
      modifiedBy:             UserMapper.mapOne(modifiedByUser),
      project:                project,
      location:               location,
      //repositoryUrl intentionally not mapped; it's not needed by the client, and shouldn't be.
      size:                   M.toNumber(w.size),
      tags:                   M.sortTags(w.tags),
      thumbnailUrl:           w.thumbnailUrl,
      updatedAt:              M.toDate(w.updatedAt),
      usageInfo:              w.usageInfo || <IWorkbookUsageInfo>{},
      usageInfoMaxValues:     usageInfoMaxValues || <IWorkbookUsageInfo>{},
      contentType:            'workbook',
      hasEncryptableExtracts: w.hasEncryptableExtracts
    };
  }

  export function mapDetail(w: IWorkbookDetail): IWorkbookDetailInfo {
    return {
      createdAt:              M.toDate(w.createdAt),
      description:            w.description,
      displayTabs:            w.displayTabs,
      downloadUrl:            w.downloadUrl,
      editUrl:                w.editUrl,
      extractCreationPending: w.extractCreationPending,
      extractEncryptionState: w.extractEncryptionState,
      favorite:               w.favorite,
      hasAlert:               w.hasAlert,
      hasExtracts:            w.hasExtracts,
      hasIncrementalExtract:  w.hasIncrementalExtract,
      hitsTimeSeries:         w.hitsTimeSeries,
      id:                     w.id,
      luid:                   w.luid,
      lastRefreshedAt:        M.toDate(w.lastRefreshedAt),
      name:                   w.name,
      owner:                  UserMapper.mapOne(w.owner),
      modifiedBy:             UserMapper.mapOne(w.modifiedByUser),
      project:                w.project ? ProjectMapper.mapName(w.project) : null,
      size:                   M.toNumber(w.size),
      tags:                   M.sortTags(w.tags),
      thumbnailUrl:           w.thumbnailUrl,
      updatedAt:              M.toDate(w.updatedAt),
      contentType:            'workbook',
      hasEncryptableExtracts: w.hasEncryptableExtracts,
      editExtractDraftUrl:    w.editExtractDraftUrl,
      workbookDataFreshness:  w.dataFreshnessViewModel,
    };
  }

  export function mapFromCRASS(content: any): IWorkbookInfo{
    return {
        defaultViewId:          null,
        description:            content.description,
        displayTabs:            content.displayTabs,
        downloadUrl:            null, // M.getDownloadWorkbookUrl() needs site prefix
        extractCreationPending: content.extractCreationPending,
        extractEncryptionState: content.extractEncryptionState,
        favorite:               false,
        hasAlert:               content.hasAlert !== 'none', // missing additional visibility checks
        hasExtracts:            content.hasExtracts,
        hasIncrementalExtract:  content.hasIncrementalExtract,
        id:                     content.id + '',
        luid:                   content.luid,
        lastRefreshedAt:        M.toDate(content.extractUpdatedAt) || M.toDateFromTimeOffsetObject(content.extractUpdatedAt),
        name:                   content.title,
        numSheets:              M.toNumber(content.viewCount),
        //repositoryUrl intentionally not mapped; it's not needed by the client, and shouldn't be.
        size:                   M.toNumber(content.size),
        tags:                   M.sortTags(content.tags),
        thumbnailUrl:           getWorkbookThumbnailUrl(content.id, (M.toDate(content.modifiedTime) || M.toDateFromTimeOffsetObject(content.modifiedTime)), content.defaultViewThumbnailId),
        updatedAt:              M.toDate(content.modifiedTime) || M.toDateFromTimeOffsetObject(content.modifiedTime),
        usageInfoMaxValues:     <IWorkbookUsageInfo>{},
        contentType:            'workbook',
        hasEncryptableExtracts: content.hasEncryptableExtracts,
        project:                {
          id: content.projectId ?? content.parentProjectId,
          name: content.containerName
        },
        usageInfo:              <IWorkbookUsageInfo>{
            ...MapperHelper.mapCRASSUsageInfo(content),
            hitsLastTwoWeeksTotal: content.hitsLastTwoWeeksTotal,
            subscriptionsTotal: content.subscriptionsTotal
        },
        owner: UserMapper.mapOneOwnerFromCRASS(content),
        location: LocationMapper.mapLocationFromCrass(content)
      };
  }

  export function mapParents(
    serverApiWorkbooks: IWorkbookParent[],
    users: _.Dictionary<IUserInfo>,
    projects: _.Dictionary<INamedItemInfo> = {},
    locations: ILocationInfo[] = [],
    favoriteWorkbooks: _.Dictionary<any> = {}
  ): IWorkbookParentInfo[] {
    const workbooksToProject = ContentMapper.getContentToProjectMap(ContentMapper.ContentTypeToTargetMap.workbook, projects);
    const workbooksToLocation = ContentMapper.getContentToLocationMap(ContentMapper.ContentTypeToTargetMap.workbook, locations);
    return _.map(serverApiWorkbooks, (w: IWorkbookParent) =>
      mapParent(w, users[w.ownerId], (FeatureFlags.refactorProjectEnabled) ? workbooksToProject[w.id] : projects[w.projectId],
      (FeatureFlags.personalSpaceEnabled) ? workbooksToLocation[w.id] : undefined,
      !!favoriteWorkbooks[w.id]));
  }

  export function mapParent(
    w: IWorkbookParent,
    owner: IUserInfo,
    project?: INamedItemInfo,
    location?: ILocationInfo,
    favorite?: boolean
  ): IWorkbookParentInfo {
    return {
      displayTabs:         w.displayTabs,
      favorite:            favorite,
      id:                  w.id,
      name:                w.name,
      numSheets:           M.toNumber(w.sheetCount),
      owner:               UserMapper.mapOne(owner),
      project:             project,
      location:            location
    };
  }

  function getWorkbookThumbnailUrl(workbookId: String, updatedAt: Date, thumbnailId: string){
    const cacheValue = thumbnailId ? thumbnailId : updatedAt ? updatedAt.getTime() : '';
    return M.REST_API_BASE_URL + `workbooks/${workbookId}/thumbnail?${cacheValue}`;
  }

  function getDownloadWorkbookUrl(repoUrl: String, sitePrefix: String){
    return `${sitePrefix}}/workbooks/${repoUrl}.twb`;
  }

}
